/**
 * This file contains utilities for using client hints for user preference which
 * are needed by the server, but are only known by the browser.
 */
import { useEffect } from "react";
import { getHintUtils } from "@epic-web/client-hints";
import {
  clientHint as colorSchemeHint,
  subscribeToSchemeChange,
} from "@epic-web/client-hints/color-scheme";
import { clientHint as timeZoneHint } from "@epic-web/client-hints/time-zone";
import { useRevalidator } from "react-router";
import { useRequestInfo } from "./request-info";

const hintsUtils = getHintUtils({
  theme: colorSchemeHint,
  timeZone: timeZoneHint,
  // add other hints here
});

export const { getHints } = hintsUtils;

/**
 * @returns an object with the client hints and their values
 */
export const useHints = () => {
  const requestInfo = useRequestInfo();
  return requestInfo.hints;
};

/**
 * @returns inline script element that checks for client hints and sets cookies
 * if they are not set then reloads the page if any cookie was set to an
 * inaccurate value.
 */
export const ClientHintCheck = ({ nonce }: { nonce?: string }) => {
  const { revalidate } = useRevalidator();
  useEffect(() => subscribeToSchemeChange(() => revalidate()), [revalidate]);

  // Only add nonce in production when CSP is active
  const scriptProps: { nonce?: string; dangerouslySetInnerHTML: { __html: string } } = {
    dangerouslySetInnerHTML: {
      __html: hintsUtils.getClientHintCheckScript(),
    },
  };
  
  // Only add nonce in production or when explicitly provided with a value
  if (nonce && nonce.trim() && process.env.NODE_ENV === 'production') {
    scriptProps.nonce = nonce;
  }

  return <script {...scriptProps} />;
};
